// 굵은 회전 베기 고리(2026-10-27 전투 액션 VFX, docs/ACTION-VFX-REF.md 2절 마무리 4타·회전 베기).
// 레퍼런스 A의 대표 장면: 몸을 감는 두꺼운 원형 고리가 0.08초에 한 바퀴 그려지고(앞머리 흰 심) 꼬리부터 지워지며 노이즈로 부서진다.
// 띠 4겹: 0 굵은 주 띠 / 1 안쪽 가는 띠 / 2 바깥 메아리 / 3 안쪽 바람 줄(옅은 줄무늬). 저사양(variant 1)은 2·3겹을 접는다(모양은 유지).
// 쓰는 칸: length = 바깥 반경(m), width = 주 띠 폭(반경 대비), roll = 평면 기울기, yaw = 기울기 축, start·sweep = 시작각·쓸어 지나가는 각,
//          tail = 한 바퀴 그리는 시간(초), life·color(몸통)·color2(심)·intensity·seed·variant. 드로우콜 1.
// 프레임 루프에서 객체를 만들지 않는다(BatchModule 슬롯 재사용).
import * as THREE from 'three/webgpu';
import { abs, clamp, float, floor, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type BatchLayout, type Slot } from '../modules/batch';
import { fxMaterial } from '../modules/material';
import { clamp01, easeOut, planeFrame } from '../modules/path';
import type { ActionModuleKind } from '../modules/types';

export const SLASH_RING_SEG = 56;
const STRIPS = 4;
/** 띠별 반경 비율·폭 비율(주 띠 폭 대비)·앞머리 지연(u)·밝기 */
const STRIP_R = [1.0, 0.74, 1.1, 0.5] as const;
const STRIP_W = [1.0, 0.32, 0.16, 0.8] as const;
const STRIP_LAG = [0, 0.05, 0.09, 0.03] as const;
export const SLASH_RING_CAP = 8;
/** 나이 0의 그려진 진행(첫 정지 프레임에 보이는 고리 몫) */
export const RING_HEAD0 = 0.22;

/** v = 띠번호 × 2 + (안쪽 0 → 바깥 1). 셰이더가 floor(v / 2)로 띠를 가른다 */
function ringLayout(seg: number): BatchLayout {
  const per = (seg + 1) * 2;
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let s = 0; s < STRIPS; s++) {
    for (let i = 0; i <= seg; i++) uvs.push(i / seg, s * 2, i / seg, s * 2 + 1);
    for (let i = 0; i < seg; i++) {
      const a = s * per + i * 2, b = a + 1, c = a + 2, d = a + 3;
      indices.push(a, c, b, b, c, d);
    }
  }
  return { verts: per * STRIPS, indices, uv: uvs };
}

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z, head = ctl.w;
  const u = uv().x, vv = uv().y;
  const strip = floor(vv.mul(0.5));
  const lv = vv.sub(strip.mul(2)); // 띠 안 0 안쪽 → 1 바깥
  const isMain = float(1).sub(strip.min(1));
  const isWind = smoothstep(2.5, 3.0, strip);
  // 앞머리(띠마다 조금씩 늦게)와 지워지는 꼬리
  const h = head.sub(strip.mul(0.03));
  const reveal = float(1).sub(smoothstep(h.sub(0.03), h, u));
  const lead = smoothstep(h.sub(0.22), h, u).mul(reveal);
  const tailPos = clamp(age.sub(0.22).mul(1.7), 0, 1.4);
  const fadeTail = smoothstep(tailPos.sub(0.3), tailPos, u);
  // 단면: 바깥 가장자리가 가장 밝은 초승달(안쪽은 투명으로)
  const body = smoothstep(0.0, 0.5, lv).mul(float(1).sub(smoothstep(0.95, 1.0, lv)));
  const rim = smoothstep(0.84, 0.94, lv).mul(float(1).sub(smoothstep(0.965, 1.0, lv)));
  const thin = float(1).sub(abs(lv.mul(2).sub(1))).pow(1.5); // 가는 띠는 가운데 선
  const prof = mix(thin, body, isMain);
  // 노이즈: 결이 고리를 따라 흐르고, 끝으로 갈수록 높은 곳만 남는다(디졸브)
  const n = texture(noise, vec2(u.mul(5).sub(age.mul(1.6)), lv.mul(0.35).add(seed).add(strip.mul(0.21)))).r;
  const thr = smoothstep(0.35, 1.0, age);
  const keep = smoothstep(thr, thr.add(0.2), n.mul(0.75).add(lv.mul(0.35)));
  // 바람 줄: 촘촘한 줄무늬만 남긴다
  const windN = texture(noise, vec2(u.mul(9).sub(age.mul(3)), seed.add(0.5))).g;
  const wind = mix(float(1), smoothstep(0.48, 0.7, windN).mul(0.55), isWind);
  const gain = mix(float(0.75), float(1), isMain).mul(wind);
  const alpha = prof.mul(reveal).mul(fadeTail).mul(keep).mul(env).mul(gain);
  const hot = clamp(rim.mul(isMain).mul(0.65).add(lead.mul(0.8)), 0, 1);
  // 일반 투명 혼합: 안쪽 어두운 진홍 → 바깥 포화 진홍(SDR) → 가는 흰 금빛 심만 HDR.
  // 가산 진홍은 밝은 바닥에서 분홍으로 바랬고(PC 캡처 2026-10-27), 몸통까지 HDR이면 톤매핑에서 분홍·흰빛으로 바랬다
  // (vfx-look-art 감사 2026-11-03 kael-skill2: 고리가 옅은 분홍으로 읽힘). 몸통 밝기는 1을 넘기지 않는다.
  const bodyCol = mix(t1.rgb.mul(0.32), t1.rgb.mul(t1.w.min(1)), smoothstep(0.1, 0.75, lv));
  const col = mix(bodyCol, t2.rgb.mul(t1.w.mul(0.8)), hot).mul(n.mul(0.3).add(0.85));
  const m = fxMaterial(col, clamp(alpha.mul(1.3), 0, 1), { blending: THREE.NormalBlending });
  return m;
}

export class SlashRingModule extends BatchModule {
  readonly kind: ActionModuleKind = 'slashRing';

  constructor(noise: THREE.Texture, cap = SLASH_RING_CAP) {
    super(cap, ringLayout(SLASH_RING_SEG), makeMaterial(noise), 22, 'fx-slash-ring');
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const f = planeFrame(s.yaw, s.roll);
    const age01 = clamp01(s.age / s.life);
    // 처음 30%에 반경이 0.86 → 1로 퍼진다
    const R = s.length * s.scale * (0.86 + 0.14 * easeOut(clamp01(age01 / 0.3)));
    const W = R * s.width;
    const head = this.head(s);
    const low = s.variant >= 1;
    const per = (SLASH_RING_SEG + 1) * 6;
    for (let st = 0; st < STRIPS; st++) {
      const base = o + st * per;
      // 저사양: 메아리·바람 줄 접기(넓이 0)
      const w = low && st >= 2 ? 0 : W * STRIP_W[st];
      const rMid = R * STRIP_R[st] - W * 0.5 * (st === 0 ? 1 : 0);
      const hs = head - STRIP_LAG[st];
      for (let i = 0; i <= SLASH_RING_SEG; i++) {
        const u = i / SLASH_RING_SEG;
        const a = s.start + s.sweep * u;
        // 꼬리 쪽으로 갈수록 가늘어지는 혜성 모양(앞머리가 가장 두껍다)
        const behind = Math.max(0, hs - u);
        const taper = Math.max(0.28, 1 - behind * 0.85);
        const hw = 0.5 * w * taper;
        const ca = Math.cos(a), sa = Math.sin(a);
        const dx = f[0] * ca + f[3] * sa, dy = f[1] * ca + f[4] * sa, dz = f[2] * ca + f[5] * sa;
        const r0 = Math.max(0, rMid - hw), r1 = rMid + hw;
        const k = base + i * 6;
        out[k] = s.x + dx * r0; out[k + 1] = s.y + dy * r0; out[k + 2] = s.z + dz * r0;
        out[k + 3] = s.x + dx * r1; out[k + 4] = s.y + dy * r1; out[k + 5] = s.z + dz * r1;
      }
    }
  }

  /**
   * 그려진 진행(u): 한 바퀴 그리는 시간(tail 칸, 초)에 RING_HEAD0 → 1.15. 나이 0에서도 앞머리 몫이 보인다
   * (히트스톱 동안 영웅 시계가 멈춰 나이 0인 채로 그려진다 — Codex P2)
   */
  private head(s: Slot): number {
    return Math.min(1.15, RING_HEAD0 + (s.age / Math.max(0.02, s.tail)) * (1.15 - RING_HEAD0));
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    out[1] = Math.min(1, 0.6 + s.age / 0.02) * (1 - Math.pow(age01, 3));
    out[2] = s.seed;
    out[3] = this.head(s);
  }
}
